import React, {useEffect, useMemo, useState} from 'react';
import type {SxProps} from '@mui/material';
import Grid from '@mui/material/Grid';
import {skipToken} from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router';
import type {NoProps} from 'types';
import {useLocalization} from 'localization';
import {useGetUpdateLogQuery} from 'services/updateLog';
import {Scene} from 'components';
import type {DateRangeValue} from 'components/fields/DateRangeField';
import {DateRangeField} from 'components/fields/DateRangeField';
import {Table} from 'schema';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import {updateLogTableConfig} from 'schemas';
import type {UpdateLogRow} from 'schemas/schemas';
import {updateLogSchema} from 'schemas/schemas';
import {useDateRangeShortcuts} from './hooks';

const API_DATE_FORMAT = 'YYYY-MM-DD';
const sxContainer: SxProps = {
  maxWidth: 1200,
};

export const UpdateLogScene: React.FC<NoProps> = () => {
  const {t, tLoose} = useLocalization();
  const navigate = useNavigate();
  const shortcuts = useDateRangeShortcuts();
  const title = t('appbar.updateLog');

  const [dateRange, setDateRange] = useState<DateRangeValue>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  const {paginationModel, resetPaginationModel} = useColumnConfigState(
    updateLogSchema.name,
  );

  const from = dateRange[0]?.format(API_DATE_FORMAT);
  const to = dateRange[1]?.format(API_DATE_FORMAT);

  const {data, isFetching} = useGetUpdateLogQuery(
    from && to
      ? {
          from,
          to,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }
      : skipToken,
  );

  // Reset to the first page whenever the selected period changes.
  useEffect(() => {
    resetPaginationModel();
  }, [from, to, resetPaginationModel]);

  const rows = useMemo<UpdateLogRow[]>(
    () =>
      (data?.items ?? []).map(item => ({
        id: `${item.ean}-${item.created}`,
        created: item.created,
        ean: item.ean,
        updatedBy: item.updatedBy,
        status: item.statusChanged
          ? tLoose(`entity.manifestation.status.full.${item.statusChanged}`)
          : undefined,
      })),
    [data, tLoose],
  );

  return (
    <Scene browserTitle={title}>
      <Scene.Header title={title} />
      <Scene.Content>
        <Grid container sx={sxContainer} spacing={2}>
          <Grid size={4}>
            <DateRangeField
              label={t('page.updateLog.dateRange.label')}
              value={dateRange}
              onChange={value => value && setDateRange(value)}
              shortcuts={shortcuts}
            />
          </Grid>
          <Grid size={12}>
            <Table<UpdateLogRow>
              schema={updateLogSchema}
              configuration={updateLogTableConfig}
              data={rows}
              rowCount={data?.elementCount ?? 0}
              onRowClick={row => {
                navigate(`/metadata/ean/${row.ean}`);
              }}
              header={''}
              loading={isFetching}
            />
          </Grid>
        </Grid>
      </Scene.Content>
    </Scene>
  );
};
